/**
 * Nosto Category Merchandising
 *
 * Calls Nosto's Search API on collection pages to apply category merchandising
 * rules and reorder the product grid according to Nosto's ranked results.
 *
 * Hooks into the existing facets.js AJAX filter flow via MutationObserver so
 * merchandised ordering is re-applied after every filter/sort update.
 *
 * Skips ordering when:
 *  - The user has explicitly changed the sort order away from the default.
 *  - The page number is greater than 1.
 *  - nostojs does not become available within NOSTO_TIMEOUT_MS.
 *  - api.search is not available (Search module not enabled on account).
 */

(function () {
  'use strict';

  var NOSTO_TIMEOUT_MS = 3000;
  var nostoCheckIntervalMs = 100;

  // ─── Bootstrap ────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    var metaEl = document.getElementById('nosto-category-meta');
    if (!metaEl) return;

    var categoryId = metaEl.getAttribute('data-category-id');
    var categoryPath = metaEl.getAttribute('data-category-title') || metaEl.getAttribute('data-category-path').replace(/^\/collections\//, '');
    var defaultSort = metaEl.getAttribute('data-default-sort');

    if (!categoryId || !categoryPath) return;

    // Skip when the user has explicitly chosen a non-default sort order.
    var urlParams = new URLSearchParams(window.location.search);
    var activeSortBy = urlParams.get('sort_by');
    if (activeSortBy && activeSortBy !== defaultSort) return;

    // Skip on page 2+. DOM-reorder only makes sense when Nosto and Shopify
    // are operating on the same set of products (page 1).
    var currentPage = parseInt(urlParams.get('page') || '1', 10);
    if (currentPage > 1) return;

    applyNostoOrdering(categoryId, categoryPath);
    observeProductGridChanges(categoryId, categoryPath, defaultSort);
  }

  // ─── Core ordering logic ──────────────────────────────────────────────────

  /**
   * Fetches Nosto's merchandised product order and reorders the product grid.
   *
   * categoryId   = Shopify collection numeric ID  (data-category-id)
   * categoryPath = URL path, e.g. /collections/mugs (data-category-path)
   */
  function applyNostoOrdering(categoryId, categoryPath) {
    var productList = document.getElementById('product-grid');
    if (!productList) return;

    var productCards = productList.querySelectorAll('li[data-product-id]');
    if (!productCards.length) return;

    // Show Shopify's built-in loading overlay while waiting for Nosto.
    var collectionEl = productList.closest('.collection');
    if (collectionEl) collectionEl.classList.add('loading');

    var resolved = false;

    // Fallback: remove loading state if Nosto doesn't respond in time.
    var fallbackTimeout = setTimeout(function () {
      if (!resolved) {
        resolved = true;
        console.warn('[Nosto] Category merchandising timed out, using default order.');
        if (collectionEl) collectionEl.classList.remove('loading');
      }
    }, NOSTO_TIMEOUT_MS);

    waitForNostoJs(
      function () {
        window.nostojs(function (api) {
          // Guard: Search module may not be enabled on this Nosto account.
          if (typeof api.search !== 'function') {
            if (!resolved) {
              resolved = true;
              clearTimeout(fallbackTimeout);
              console.warn('[Nosto] api.search is not available. Ensure the Search module is enabled for your account.');
              if (collectionEl) collectionEl.classList.remove('loading');
            }
            return;
          }

          api
            .search(
              {
                products: {
                  // categoryId: Shopify numeric collection ID
                  categoryId: categoryId,
                  // categoryPath: URL path that matches the category field in Nosto's product index
                  // (for Shopify stores this is the collection URL, e.g. /collections/mugs)
                  categoryPath: categoryPath,
                  fields: ['productId'],
                  size: parseInt(document.getElementById('nosto-category-meta').getAttribute('data-total-products') || '200', 10),
                  from: 0,
                },
              },
              {
                track: 'category',
                redirect: false,
              }
            )
            .then(function (result) {
              if (resolved) return;
              resolved = true;
              clearTimeout(fallbackTimeout);

              var hits =
                result &&
                result.products &&
                result.products.hits;

              if (hits && hits.length) {
                var orderedIds = hits.map(function (hit) {
                  // Normalise: strip any "shopify-" prefix Nosto may add.
                  return String(hit.productId).replace(/^shopify-/, '');
                });
                reorderProductCards(productList, orderedIds);
              }

              if (collectionEl) collectionEl.classList.remove('loading');
            })
            .catch(function (err) {
              if (resolved) return;
              resolved = true;
              clearTimeout(fallbackTimeout);
              console.warn('[Nosto] Category merchandising error:', err);
              if (collectionEl) collectionEl.classList.remove('loading');
            });
        });
      },
      NOSTO_TIMEOUT_MS
    );
  }

  /**
   * Reorders <li data-product-id> elements inside productList to match
   * the order of IDs returned by Nosto. Products not found in the DOM
   * (e.g. filtered out) are silently skipped.
   */
  function reorderProductCards(productList, orderedIds) {
    var cardMap = {};
    productList.querySelectorAll('li[data-product-id]').forEach(function (li) {
      var rawId = li.getAttribute('data-product-id');
      cardMap[String(rawId).replace(/^shopify-/, '')] = li;
    });

    orderedIds.forEach(function (id) {
      if (cardMap[id]) {
        productList.appendChild(cardMap[id]);
      }
    });
  }

  // ─── MutationObserver for AJAX filter updates ─────────────────────────────

  /**
   * Watches #ProductGridContainer for DOM replacements triggered by
   * facets.js (AJAX filtering / sorting). Re-applies Nosto ordering after
   * each replacement, unless the user has changed the sort order.
   */
  function observeProductGridChanges(categoryId, categoryPath, defaultSort) {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length) {
          var urlParams = new URLSearchParams(window.location.search);
          var activeSortBy = urlParams.get('sort_by');
          if (activeSortBy && activeSortBy !== defaultSort) break;

          applyNostoOrdering(categoryId, categoryPath);
          break;
        }
      }
    });

    observer.observe(container, { childList: true, subtree: false });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Polls until window.nostojs is defined (or its queue stub is in place),
   * then calls callback. Gives up after maxWaitMs milliseconds.
   *
   * In most cases nostojs is defined immediately as the Nosto app embed
   * injects a queue stub before any section scripts run, so this is
   * effectively instant.
   */
  function waitForNostoJs(callback, maxWaitMs) {
    if (window.nostojs) {
      callback();
      return;
    }
    var startTime = Date.now();
    var interval = setInterval(function () {
      if (window.nostojs) {
        clearInterval(interval);
        callback();
      } else if (Date.now() - startTime >= maxWaitMs) {
        clearInterval(interval);
      }
    }, nostoCheckIntervalMs);
  }
})();
